import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { SettingsNav } from "@/pages/settings/components/SettingsNav";
import { AdminOnly } from "@/components/feature/AdminOnly";
import { money, fmtDateTime } from "@/lib/format";

interface UsageLog {
  id: string;
  task_id: string | null;
  provider_code: string | null;
  model_name: string | null;
  input_tokens: number;
  output_tokens: number;
  estimated_cost: number;
  status: string;
  context_refs: Record<string, unknown>;
  created_at: string;
}

interface Budget {
  id: string;
  monthly_limit: number;
  warning_threshold: number;
  current_usage: number;
  period: string;
}

interface Task {
  id: string;
  task_id: string;
  purpose: string | null;
  allowed_roles: string[];
  requires_external: boolean;
  model_class: string;
  max_input_tokens: number;
  max_output_tokens: number;
  requires_approval: boolean;
  enabled: boolean;
}

interface Provider {
  id: string;
  provider_code: string;
  name: string;
  model_class: string;
  model_name: string;
  input_cost_per_1k: number;
  output_cost_per_1k: number;
  enabled: boolean;
}

interface Approval {
  id: string;
  task_id: string | null;
  title: string | null;
  summary: string | null;
  payload: Record<string, unknown> | null;
  status: string;
  requested_by: string | null;
  created_at: string;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function AiControlCentre() {
  const { org } = useAuth();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [budgetForm, setBudgetForm] = useState({ monthly_limit: "100", warning_threshold: "70" });
  const [approvalBusy, setApprovalBusy] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState("");
  const [aiDisableBusy, setAiDisableBusy] = useState(false);
  const [aiDisableConfirmOpen, setAiDisableConfirmOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["ai-control", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [logs, budget, tasks, providers, approvals, orgRow] = await Promise.all([
        supabase
          .from("ai_usage_logs")
          .select("id, task_id, provider_code, model_name, input_tokens, output_tokens, estimated_cost, status, context_refs, created_at")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(100),
        supabase
          .from("ai_budgets")
          .select("id, monthly_limit, warning_threshold, current_usage, period")
          .eq("org_id", org!.id)
          .maybeSingle(),
        supabase.from("ai_tasks").select("id, task_id, purpose, allowed_roles, requires_external, model_class, max_input_tokens, max_output_tokens, requires_approval, enabled").order("task_id"),
        supabase.from("ai_providers").select("id, provider_code, name, model_class, model_name, input_cost_per_1k, output_cost_per_1k, enabled").order("provider_code"),
        supabase
          .from("ai_approvals")
          .select("id, task_id, title, summary, payload, status, requested_by, created_at")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase.from("organizations").select("settings").eq("id", org!.id).maybeSingle(),
      ]);
      return {
        logs: (logs.data ?? []) as UsageLog[],
        budget: (budget.data ?? null) as Budget | null,
        tasks: (tasks.data ?? []) as Task[],
        providers: (providers.data ?? []) as Provider[],
        approvals: (approvals.data ?? []) as Approval[],
        settings: (orgRow.data as { settings?: Record<string, unknown> | null } | null)?.settings ?? {},
        aiDisabled: !!((orgRow.data as { settings?: { ai_disabled?: boolean } | null } | null)?.settings?.ai_disabled),
      };
    },
  });

  const aiDisabled = data?.aiDisabled ?? false;

  const toggleAiDisabled = async () => {
    setAiDisableBusy(true);
    const next = !aiDisabled;
    const currentSettings = (data?.settings ?? {}) as Record<string, unknown>;
    const { error: updErr } = await supabase
      .from("organizations")
      .update({
        settings: { ...currentSettings, ai_disabled: next },
        updated_at: new Date().toISOString(),
      })
      .eq("id", org!.id);
    setAiDisableBusy(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["ai-control", org?.id] });
  };

  const decideApproval = async (id: string, decision: "approved" | "rejected") => {
    setApprovalBusy(id);
    setApprovalError("");
    const { data: userData } = await supabase.auth.getUser();
    const { error: updErr } = await supabase
      .from("ai_approvals")
      .update({ status: decision, decided_by: userData.user?.id ?? null, decided_at: new Date().toISOString() })
      .eq("id", id);
    setApprovalBusy(null);
    if (updErr) {
      setApprovalError(updErr.message);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["ai-control", org?.id] });
  };

  const stats = useMemo(() => {
    const logs = data?.logs ?? [];
    const totalRequests = logs.length;
    const totalCost = logs.reduce((s, l) => s + Number(l.estimated_cost ?? 0), 0);
    const deterministic = logs.filter((l) => l.provider_code === "deterministic" || l.provider_code === "cache").length;
    const external = logs.filter((l) => l.provider_code !== "deterministic" && l.provider_code !== "cache").length;
    const totalTokens = logs.reduce((s, l) => s + Number(l.input_tokens ?? 0) + Number(l.output_tokens ?? 0), 0);
    return { totalRequests, totalCost, deterministic, external, totalTokens };
  }, [data]);

  const openEdit = () => {
    setBudgetForm({
      monthly_limit: String(data?.budget?.monthly_limit ?? 100),
      warning_threshold: String(data?.budget?.warning_threshold ?? 70),
    });
    setError("");
    setEditOpen(true);
  };

  const handleSaveBudget = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const monthlyLimit = Number(budgetForm.monthly_limit) || 0;
    const warningThreshold = Number(budgetForm.warning_threshold) || 0;
    const currentUsage = stats.totalCost;
    let err: Error | null = null;
    if (data?.budget) {
      const { error: updErr } = await supabase
        .from("ai_budgets")
        .update({ monthly_limit: monthlyLimit, warning_threshold: warningThreshold, current_usage: currentUsage, updated_at: new Date().toISOString() })
        .eq("id", data.budget.id);
      err = updErr;
    } else {
      const { error: insErr } = await supabase.from("ai_budgets").insert({
        org_id: org!.id,
        monthly_limit: monthlyLimit,
        warning_threshold: warningThreshold,
        current_usage: currentUsage,
        period: new Date().toISOString().slice(0, 7),
        updated_at: new Date().toISOString(),
      });
      err = insErr;
    }
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setEditOpen(false);
    queryClient.invalidateQueries({ queryKey: ["ai-control", org?.id] });
  };

  const monthlyLimit = Number(data?.budget?.monthly_limit ?? 100);
  const warningThreshold = Number(data?.budget?.warning_threshold ?? 70);
  const pct = monthlyLimit > 0 ? Math.min(100, (stats.totalCost / monthlyLimit) * 100) : 0;

  return (
    <AdminOnly title="AI Control Centre">
      <div>
        <PageHeader title="AI Control Centre" subtitle="Usage, cost and governance for AI-assisted features" />

        {/* Global AI disable banner */}
        <div className={`mb-6 rounded-xl border p-4 flex items-start justify-between gap-4 ${aiDisabled ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`}>
          <div className="flex-1">
            <h3 className={`text-sm font-semibold ${aiDisabled ? "text-red-800" : "text-slate-800"}`}>
              {aiDisabled ? "External AI is disabled" : "External AI is enabled"}
            </h3>
            <p className={`text-xs mt-0.5 ${aiDisabled ? "text-red-600" : "text-slate-500"}`}>
              {aiDisabled
                ? "No new external AI calls will be made. Deterministic features continue to work normally."
                : "Tasks that require an external provider will route through the cheapest enabled provider. Deterministic tasks remain free."}
            </p>
          </div>
          <button
            onClick={() => {
              if (!aiDisabled) setAiDisableConfirmOpen(true);
              else toggleAiDisabled();
            }}
            disabled={aiDisableBusy}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap border disabled:opacity-60 ${
              aiDisabled
                ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600"
                : "bg-white hover:bg-red-50 text-red-600 border-red-200"
            }`}
          >
            {aiDisableBusy ? "Saving…" : aiDisabled ? "Enable external AI" : "Disable external AI"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500">Total requests</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.totalRequests}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500">Est. cost</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{money(stats.totalCost, "USD")}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500">Deterministic / cache</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1">{stats.deterministic}</p>
                <p className="text-[11px] text-slate-400">no external call, $0</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500">External calls</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{stats.external}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Monthly budget</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Period {data?.budget?.period ?? "—"}</p>
                </div>
                <button
                  onClick={openEdit}
                  className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-pencil-line" /> Edit
                </button>
              </div>
              <div className="flex items-end justify-between mb-2">
                <span className="text-sm text-slate-600">
                  <span className="text-lg font-semibold text-slate-900">{money(stats.totalCost, "USD")}</span> used
                </span>
                <span className="text-xs text-slate-400">limit {money(monthlyLimit, "USD")}</span>
              </div>
              <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : pct >= (warningThreshold / (monthlyLimit || 1)) * 100 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Warning threshold at {money(warningThreshold, "USD")} · total tokens {stats.totalTokens.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Approval Queue</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Records of AI-generated proposals. Approvals do not execute automatically — they are reviewed and acted on manually.
                  </p>
                </div>
                <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  {(data?.approvals ?? []).filter((a) => a.status === "pending").length} pending
                </span>
              </div>
              {approvalError && (
                <div className="px-4 py-2.5 bg-red-50 border-b border-red-100 text-sm text-red-700">{approvalError}</div>
              )}
              {isLoading ? (
                <div className="p-8">
                  <LoadingState />
                </div>
              ) : !data || data.approvals.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">No AI actions awaiting approval</div>
              ) : (
                <div className="divide-y divide-slate-50">
                  {data.approvals.map((a) => {
                    const pending = a.status === "pending";
                    return (
                      <div key={a.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-800">{a.title ?? "—"}</span>
                              <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-mono">{a.task_id ?? "—"}</span>
                              {pending ? (
                                <StatusBadge status="pending" customLabel="Pending" />
                              ) : (
                                <StatusBadge status={a.status === "approved" ? "success" : "error"} customLabel={a.status === "approved" ? "Approved" : "Rejected"} />
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{a.summary ?? "—"}</p>
                            <p className="text-[11px] text-slate-400 mt-1">
                              Requested · {fmtDateTime(a.created_at)}
                            </p>
                          </div>
                          {pending && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => decideApproval(a.id, "approved")}
                                disabled={approvalBusy === a.id}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => decideApproval(a.id, "rejected")}
                                disabled={approvalBusy === a.id}
                                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800">Usage Log</h3>
              </div>
              {isLoading ? (
                <div className="p-8">
                  <LoadingState />
                </div>
              ) : isError ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-slate-500 mb-4">Couldn't load usage data.</p>
                  <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
                    Retry
                  </button>
                </div>
              ) : !data || data.logs.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">No AI requests recorded yet</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50/70 border-b border-slate-100">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Task</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Route</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tokens</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Latency</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.logs.map((l) => {
                        const deterministic = l.provider_code === "deterministic" || l.provider_code === "cache";
                        const latency = Number(l.context_refs?.latency_ms ?? 0);
                        return (
                          <tr key={l.id} className="border-b border-slate-50">
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-slate-800 font-mono">{l.task_id ?? "—"}</span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {deterministic ? (
                                  <StatusBadge status="success" customLabel="Internal" />
                                ) : (
                                  <StatusBadge status="info" customLabel={l.provider_code ?? "external"} />
                                )}
                                <span className="text-xs text-slate-500">{l.model_name ?? "—"}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 text-right">
                              {(Number(l.input_tokens) + Number(l.output_tokens)).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 text-right">{money(l.estimated_cost, "USD")}</td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {latency > 0 ? `${latency}ms` : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">{fmtDateTime(l.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800">Task Registry</h3>
                <p className="text-xs text-slate-400 mt-0.5">Platform-managed capabilities. Enable/disable is controlled by the platform administrator.</p>
              </div>
              <div className="divide-y divide-slate-50">
                {(data?.tasks ?? []).map((t) => (
                  <div key={t.id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-slate-700 font-mono">{t.task_id}</span>
                      <StatusBadge status={t.enabled ? "active" : "inactive"} customLabel={t.enabled ? "On" : "Off"} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">{t.purpose ?? "—"}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {t.requires_external ? "external" : "deterministic"}
                      </span>
                      <span className="text-[10px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full capitalize">
                        model: {t.model_class}
                      </span>
                      {t.requires_approval && (
                        <span className="text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">needs approval</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800">Providers</h3>
              </div>
              <div className="divide-y divide-slate-50">
                {(data?.providers ?? []).map((p) => (
                  <div key={p.id} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{p.name}</span>
                      <StatusBadge status={p.enabled ? "active" : "inactive"} customLabel={p.enabled ? "On" : "Off"} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      {p.model_name} · {p.model_class} · {money(p.input_cost_per_1k, "USD")}/1k in · {money(p.output_cost_per_1k, "USD")}/1k out
                    </p>
                  </div>
                ))}
                {(data?.providers ?? []).length === 0 && (
                  <div className="px-4 py-6 text-center text-sm text-slate-400">No providers configured</div>
                )}
              </div>
            </div>

            <SettingsNav />
          </div>
        </div>

        <Modal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title="Edit Budget"
          footer={
            <>
              <button
                onClick={() => setEditOpen(false)}
                className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBudget}
                disabled={saving}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
              >
                {saving ? "Saving..." : "Save Budget"}
              </button>
            </>
          }
        >
          <form onSubmit={handleSaveBudget} className="space-y-4">
            {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Monthly limit (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetForm.monthly_limit}
                  onChange={(e) => setBudgetForm((f) => ({ ...f, monthly_limit: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Warning threshold (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetForm.warning_threshold}
                  onChange={(e) => setBudgetForm((f) => ({ ...f, warning_threshold: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Current usage {money(stats.totalCost, "USD")} is computed from your recorded AI requests.
            </p>
          </form>
        </Modal>

        <ConfirmDialog
          open={aiDisableConfirmOpen}
          title="Disable external AI"
          message="This stops all external AI provider calls for this organization. Deterministic internal features will continue to work normally. You can re-enable at any time."
          confirmLabel="Disable external AI"
          onCancel={() => setAiDisableConfirmOpen(false)}
          onConfirm={() => {
            setAiDisableConfirmOpen(false);
            toggleAiDisabled();
          }}
        />
      </div>
    </AdminOnly>
  );
}